import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation, useQuery } from '@apollo/client'
import { Link as RouterLink } from 'react-router-dom'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Link from '@material-ui/core/Link'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import Switch from '@material-ui/core/Switch'
import Typography from '@material-ui/core/Typography'
import QRCode from 'qrcode.react'
import { get } from 'lodash'
import { PGA_COM_HOST } from 'env'
import { useAuth } from 'lib/auth'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import UpgradeSuccessModal from 'components/upgrade-success-modal'
import { formatNameForDisplay } from 'lib/text-display'
import { secureLink } from 'lib/utils'
import { ADMTrainingExternalLink } from 'lib/pga-dot-coach'
import StatusModal from 'components/status-modal'
import ButtonMenu from 'components/button-menu'
import { IconDownload } from 'components/atoms/icons'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import OpenInNewOutlinedIcon from 'components/material-symbol/icons/OpenInNew'
import ErrorOutlineIcon from 'components/material-symbol/icons/ErrorOutline'
import ReportProblemOutlinedIcon from 'components/material-symbol/icons/ReportProblem'
import Tooltip from '@material-ui/core/Tooltip'
import TooltipForDisabledComponent from 'components/tooltip-for-disabled-component'
import RoomOutlinedIcon from 'components/material-symbol/icons/Room'
import ProfileActions from '../profile-actions'
import CoachProfileEmptyState from '../coach-profile-empty-state'
import { useStyles as useProfilePageStyles } from '../styles'
import DateUtils, { datesForRelativeWindow } from 'utils/dateUtils'
import { ToggleCoachStatus } from 'pages/pga-coach/profile/index/query'
import { PROFILE_OVERVIEW } from './query'
import useStyles from './styles'
import {
  countInDateRange,
  getFacilityLocation,
  getPercentChange,
  getPromotionStatus,
  getStatusBanner,
  getVisibilityCaption,
  getVisibilityDisabledTooltip,
} from './utils'
import {
  COACH_PLUS_CONVERSION_MULTIPLIER,
  COACH_PLUS_PROFILE_CONVERSION_RATE,
  STANDARD_PROFILE_CONVERSION_RATE,
} from './constants'

const QR_CODE_ID = 'coach-profile-overview-qr-code'

const ANALYTICS_WINDOW_OPTIONS = [
  { value: DateUtils.THIS_WEEK, label: 'This week' },
  { value: DateUtils.LAST_WEEK, label: 'Last week' },
  { value: DateUtils.THIS_MONTH, label: 'This month' },
  { value: DateUtils.LAST_MONTH, label: 'Last month' },
]

const getAnalyticsWindowLabel = value =>
  ANALYTICS_WINDOW_OPTIONS.find(option => option.value === value)?.label ??
  'This month'

const downloadProfileQr = () => {
  const canvas = document.getElementById(QR_CODE_ID)
  if (!canvas) return

  const pngUrl = canvas
    .toDataURL('image/png')
    .replace('image/png', 'image/octet-stream')
  const downloadLink = document.createElement('a')
  downloadLink.href = pngUrl
  downloadLink.download = 'qr-code.png'
  document.body.appendChild(downloadLink)
  downloadLink.click()
  document.body.removeChild(downloadLink)
}

const getPreviousDateRange = analyticsWindow => {
  switch (analyticsWindow) {
    case DateUtils.THIS_WEEK: {
      const lastWeek = datesForRelativeWindow(DateUtils.LAST_WEEK)
      return {
        startDate: lastWeek.startDate,
        endDate: lastWeek.endDate,
      }
    }
    case DateUtils.LAST_WEEK: {
      const lastWeekStart = datesForRelativeWindow(DateUtils.LAST_WEEK)
        .startDate
      const previousWeekStart = lastWeekStart.minus({ weeks: 1 })
      return {
        startDate: previousWeekStart.startOf('week'),
        endDate: previousWeekStart.endOf('week'),
      }
    }
    case DateUtils.LAST_MONTH: {
      const lastMonthStart = datesForRelativeWindow(DateUtils.LAST_MONTH)
        .startDate
      const previousMonthStart = lastMonthStart.minus({ months: 1 })
      return {
        startDate: previousMonthStart.startOf('month'),
        endDate: previousMonthStart.endOf('month'),
      }
    }
    case DateUtils.THIS_MONTH:
    default: {
      const lastMonth = datesForRelativeWindow(DateUtils.LAST_MONTH)
      return {
        startDate: lastMonth.startDate,
        endDate: lastMonth.endDate,
      }
    }
  }
}

const enableProfileTxt = {
  title: 'Enable Profile?',
  body1: 'Are you sure you want to enable your PGA.com coach profile?',
  body2:
    'Selecting "Enable" below will make your Coach Profile visible on PGA.com',
  confirmLabel: 'Enable',
}

const disableProfileTxt = {
  title: 'Disable Profile?',
  body1: 'Are you sure you want to disable your PGA.com coach profile?',
  body2:
    'Selecting "Disable" below will remove your Coach Profile from public view on PGA.com',
  confirmLabel: 'Disable',
}

const useToggleCoachStatus = (status, coachProfileId, toggleCoachStatus) => {
  const [active, toggleStatusFn] = useState(status === 'ACTIVE')
  const toggleStatus = async (setOpen, setLoading, setError) => {
    try {
      setLoading(true)
      const { error } = await toggleCoachStatus({
        variables: { coachProfileId },
      })
      setLoading(false)
      if (error) setError(error)
      toggleStatusFn(!active)
      setOpen(false)
    } catch (error) {
      setError(error)
      setLoading(false)
    }
  }

  return [active, toggleStatus]
}

const MetricChange = ({ current, previous, classes }) => {
  const change = getPercentChange(current, previous)

  if (change === null) {
    return (
      <span
        className={`${classes.statusBadge} ${classes.metricChangePositive}`}
      >
        ↗ New
      </span>
    )
  }

  if (change === 0) {
    return (
      <span className={`${classes.statusBadge} ${classes.metricChangeNeutral}`}>
        — 0%
      </span>
    )
  }

  if (change > 0) {
    return (
      <span
        className={`${classes.statusBadge} ${classes.metricChangePositive}`}
      >
        ↗ +{change}%
      </span>
    )
  }

  return (
    <span className={`${classes.statusBadge} ${classes.metricChangeNegative}`}>
      ↘ {Math.abs(change)}%
    </span>
  )
}

MetricChange.propTypes = {
  current: PropTypes.number,
  previous: PropTypes.number,
  classes: PropTypes.object.isRequired,
}

const MetricCard = ({ label, value, previousValue, loading, classes }) => (
  <Box className={classes.metricCard}>
    <Typography className={classes.metricLabel} component="div">
      {label}
    </Typography>
    {loading ? (
      <CircularProgress size={28} />
    ) : (
      <Typography className={classes.metricValue} component="div">
        {Number.isNaN(value) ? '—' : value}
      </Typography>
    )}
    {!loading ? (
      <MetricChange
        current={value}
        previous={previousValue}
        classes={classes}
      />
    ) : null}
  </Box>
)

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  previousValue: PropTypes.number,
  loading: PropTypes.bool,
  classes: PropTypes.object.isRequired,
}

const PromotionStatusBadge = ({ coachProfile, facilities, classes }) => {
  const status = getPromotionStatus({ coachProfile, facilities })

  return (
    <Tooltip title={status.tooltip} arrow>
      <span
        className={`${classes.statusBadge} ${classes[status.styleKey]}`}
        data-testid="coach-promotion-status"
      >
        {status.label}
      </span>
    </Tooltip>
  )
}

PromotionStatusBadge.propTypes = {
  coachProfile: PropTypes.object.isRequired,
  facilities: PropTypes.array,
  classes: PropTypes.object.isRequired,
}

const VisibilityToggle = ({
  coachProfile,
  facilities,
  toggleCoachStatus,
  classes,
}) => {
  const [open, setOpen] = useState(false)
  const [active, toggleStatus] = useToggleCoachStatus(
    coachProfile.status,
    coachProfile.coachProfileId,
    toggleCoachStatus,
  )
  const { coach } = coachProfile
  const visibilityDisabled = !coach.canPromoteContent
  const visibilityDisabledTooltip = getVisibilityDisabledTooltip(
    coach.canPromoteContent,
  )
  const switchControl = (
    <Switch
      checked={coach.canPromoteContent ? active : false}
      onChange={() => setOpen(!open)}
      name="visible"
      color="primary"
      disabled={visibilityDisabled}
    />
  )

  return (
    <Box className={classes.visibilitySection}>
      <Box className={classes.visibilityCopy}>
        <Typography className={classes.visibilityTitle}>
          Public profile visibility
        </Typography>
        <Typography className={classes.visibilityCaption}>
          {getVisibilityCaption({ coachProfile, facilities })}
        </Typography>
      </Box>
      <FormControlLabel
        className={classes.visibilitySwitch}
        control={
          visibilityDisabled && visibilityDisabledTooltip ? (
            <TooltipForDisabledComponent
              title={visibilityDisabledTooltip}
              arrow
              placement="top"
            >
              {switchControl}
            </TooltipForDisabledComponent>
          ) : (
            switchControl
          )
        }
        label=""
      />
      <StatusModal
        text={active ? disableProfileTxt : enableProfileTxt}
        open={open}
        setOpen={setOpen}
        toggleStatus={toggleStatus}
      />
    </Box>
  )
}

VisibilityToggle.propTypes = {
  coachProfile: PropTypes.object.isRequired,
  facilities: PropTypes.array,
  toggleCoachStatus: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
}

const isRenderableCoachProfile = coachProfile =>
  Boolean(coachProfile?.coachProfileId && coachProfile?.coach)

const ProfileOverview = ({ hasCoachProfile = true }) => {
  const classes = useStyles()
  const pageClasses = useProfilePageStyles()
  const { user } = useAuth()
  const [analyticsWindow, setAnalyticsWindow] = useState(DateUtils.THIS_MONTH)

  const {
    startDate,
    endDate,
    previousStartDate,
    previousEndDate,
  } = useMemo(() => {
    const currentRange = datesForRelativeWindow(analyticsWindow)
    const previousRange = getPreviousDateRange(analyticsWindow)

    return {
      startDate: currentRange.startDate,
      endDate: currentRange.endDate,
      previousStartDate: previousRange.startDate,
      previousEndDate: previousRange.endDate,
    }
  }, [analyticsWindow])

  const { data, loading, refetch } = useQuery(PROFILE_OVERVIEW, {
    skip: !hasCoachProfile,
    variables: {
      startDate: startDate.toISODate(),
      endDate: endDate.toISODate(),
      previousStartDate: previousStartDate.toISODate(),
      previousEndDate: previousEndDate.toISODate(),
    },
  })

  const {
    showSuccessModal,
    upgradeLoading,
    handleUpgradeClick,
    handleDismiss,
  } = useUpgradeFlow('profile-overview')

  const [toggleCoachStatus] = useMutation(ToggleCoachStatus, {
    onCompleted: refetch,
  })

  if (!hasCoachProfile) {
    return (
      <Box className={pageClasses.emptyStateContent}>
        <CoachProfileEmptyState />
      </Box>
    )
  }

  const hasPaidTier =
    get(data, 'currentUser.attributes.hasPaidTierSubscription') ??
    user?.coach?.hasPaidTierSubscription

  const coachProfile = get(data, 'currentUser.attributes.coachProfile')
  const facilities = get(data, 'currentUser.attributes.facilities', [])
  const coachingInquiries = data?.coachingInquiries || []
  const currentAnalytics = data?.currentUser?.coachProfileAnalytics || {}
  const previousAnalytics =
    data?.currentUser?.previousCoachProfileAnalytics || {}

  if (!loading && !isRenderableCoachProfile(coachProfile)) {
    return (
      <Box className={pageClasses.emptyStateContent}>
        <CoachProfileEmptyState />
      </Box>
    )
  }

  const coachName = formatNameForDisplay(
    `${coachProfile?.firstName || ''} ${coachProfile?.lastName || ''}`.trim(),
  )
  const location = getFacilityLocation(coachProfile, facilities)
  const hasActiveCoachProfile = coachProfile?.status === 'ACTIVE'
  const coachProfileUrl = coachProfile?.customSlug
    ? `${PGA_COM_HOST}/coach/${coachProfile.customSlug}?from-qr-code=true`
    : null

  const currentLeads = countInDateRange(coachingInquiries, startDate, endDate)
  const previousLeads = countInDateRange(
    coachingInquiries,
    previousStartDate,
    previousEndDate,
  )

  const banner = coachProfile
    ? getStatusBanner({
      coachProfile,
      facilities,
      classes,
      InfoIcon: InfoOutlinedIcon,
      ErrorIcon: ErrorOutlineIcon,
      WarningIcon: ReportProblemOutlinedIcon,
      Link,
      RouterLink,
      ADMTrainingExternalLink,
    })
    : null

  return (
    <>
      <Box className={classes.card} data-testid="coach-profile-overview">
        <Box className={classes.cardBody}>
          {loading ? (
            <Box className={classes.loading}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {banner ? (
                <Box className={`${classes.banner} ${banner.className}`}>
                  {banner.icon}
                  <Typography variant="body2" component="div">
                    {banner.text}
                  </Typography>
                </Box>
              ) : null}

              <Box className={classes.profileHeader}>
                <Box className={classes.avatarWrapper}>
                  <Avatar
                    aria-label="coach"
                    src={secureLink(coachProfile.profilePhoto)}
                    className={classes.avatar}
                  >
                    {!coachProfile.profilePhoto &&
                      `${coachProfile.firstName?.substring(0, 1) ||
                        ''}${coachProfile.lastName?.substring(0, 1) || ''}`}
                  </Avatar>
                </Box>

                <Box className={classes.profileDetails}>
                  <Box className={classes.nameRow}>
                    <Typography className={classes.name}>
                      {coachName}
                    </Typography>
                    <PromotionStatusBadge
                      coachProfile={coachProfile}
                      facilities={facilities}
                      classes={classes}
                    />
                  </Box>
                  {location ? (
                    <Box className={classes.locationRow}>
                      <RoomOutlinedIcon className={classes.locationIcon} />
                      <span>{location}</span>
                    </Box>
                  ) : null}
                  <ProfileActions
                    customSlug={coachProfile.customSlug}
                    className={classes.profileActions}
                  />
                </Box>

                <Box className={classes.profileTrailing}>
                  {hasActiveCoachProfile && coachProfileUrl ? (
                    <Box className={classes.qrColumn}>
                      <QRCode
                        value={coachProfileUrl}
                        id={QR_CODE_ID}
                        data-testid="coach-profile-overview-qr-code"
                        size={88}
                        level="H"
                        className={classes.qrCode}
                      />
                      <Button
                        className={classes.qrDownload}
                        onClick={downloadProfileQr}
                        startIcon={<IconDownload />}
                      >
                        QR
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              </Box>

              <VisibilityToggle
                coachProfile={coachProfile}
                facilities={facilities}
                toggleCoachStatus={toggleCoachStatus}
                classes={classes}
              />

              <Box>
                <Box className={classes.performanceHeader}>
                  <Typography
                    component="p"
                    variant="inherit"
                    className={classes.performanceTitle}
                  >
                    Performance snapshot
                  </Typography>
                  <ButtonMenu
                    label={getAnalyticsWindowLabel(analyticsWindow)}
                    buttonProps={{
                      variant: 'outlined',
                      color: 'default',
                      className: classes.periodButton,
                      'aria-label': 'Performance period',
                    }}
                    PopoverClasses={{ paper: classes.periodMenu }}
                    render={({ handleClose }) => (
                      <MenuList aria-label="Performance period options">
                        {ANALYTICS_WINDOW_OPTIONS.map(option => (
                          <MenuItem
                            key={option.value}
                            selected={analyticsWindow === option.value}
                            onClick={() => {
                              setAnalyticsWindow(option.value)
                              handleClose()
                            }}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    )}
                  />
                </Box>
                <Box className={classes.metricsGrid}>
                  <MetricCard
                    label="Impressions"
                    value={currentAnalytics.impressions}
                    previousValue={previousAnalytics.impressions}
                    loading={loading}
                    classes={classes}
                  />
                  <MetricCard
                    label="Views"
                    value={currentAnalytics.views}
                    previousValue={previousAnalytics.views}
                    loading={loading}
                    classes={classes}
                  />
                  <MetricCard
                    label="Leads"
                    value={currentLeads}
                    previousValue={previousLeads}
                    loading={loading}
                    classes={classes}
                  />
                </Box>
              </Box>

              {!hasPaidTier ? (
                <Box className={classes.coachPlusSection}>
                  <Box className={classes.coachPlusCopy}>
                    <Typography className={classes.coachPlusTitle}>
                      Did You Know?
                    </Typography>
                    <Typography className={classes.coachPlusDetail}>
                      PGA Coach+ profiles convert{' '}
                      <strong>{COACH_PLUS_CONVERSION_MULTIPLIER}</strong> better
                      than standard profiles.{' '}
                      <strong>{COACH_PLUS_PROFILE_CONVERSION_RATE}</strong> vs.{' '}
                      <strong>{STANDARD_PROFILE_CONVERSION_RATE}</strong>.
                      Upgrade today to get more from every profile visit.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpgradeClick}
                    disabled={upgradeLoading}
                    className={classes.coachPlusButton}
                    startIcon={<OpenInNewOutlinedIcon />}
                  >
                    {upgradeLoading ? 'Loading...' : 'Upgrade'}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </Box>
      </Box>
      <UpgradeSuccessModal isOpen={showSuccessModal} onClose={handleDismiss} />
    </>
  )
}

export default ProfileOverview

ProfileOverview.propTypes = {
  hasCoachProfile: PropTypes.bool,
}
